import React from 'react';
import {
    Box,
    Modal,
    Typography,
    Stack,
    useMediaQuery,
    Alert,
    Divider,
    IconButton,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import MailIcon from '@mui/icons-material/Mail';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CakeIcon from '@mui/icons-material/Cake';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import { ActionButton, CancelButton } from '@/components/Button';
import { VerificationResponse } from '@/services/verificationService';

interface VerificationResultsDialogProps {
    open: boolean;
    onClose: () => void;
    onCloseAll: () => void; // Close all dialogs
    verificationData: VerificationResponse | null;
    showForceUpdate?: boolean; // Show Force Update button when viewing existing KYC data
    onForceUpdate?: () => void; // Callback for Force Update button
}

const VerificationResultsDialog: React.FC<VerificationResultsDialogProps> = ({
    open,
    onClose,
    onCloseAll,
    verificationData,
    showForceUpdate = false,
    onForceUpdate,
}) => {
    const isMobile = useMediaQuery('(max-width:600px)');

    const formatDate = (dateString: string): string => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-ZA', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-ZA', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const getGenderIcon = (gender: string) => {
        if (!gender) return <PersonIcon sx={{ fontSize: 16, color: '#666' }} />;
        switch (gender.toUpperCase()) {
            case 'M':
            case 'MALE':
                return <MaleIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
            case 'F':
            case 'FEMALE':
                return <FemaleIcon sx={{ fontSize: 16, color: '#d81b60' }} />;
            default:
                return <PersonIcon sx={{ fontSize: 16, color: '#666' }} />;
        }
    };

    const getGenderText = (gender: string): string => {
        if (!gender) return 'N/A';
        const upperGender = gender.toUpperCase();
        if (upperGender.includes('M') || upperGender === 'MALE') return 'Male';
        if (upperGender.includes('F') || upperGender === 'FEMALE') return 'Female';
        return gender;
    };

    const formatValue = (value: string | undefined | null): string => {
        return value && value.trim() ? value : 'N/A';
    };

    const style = {
        position: 'absolute' as const,
        top: isMobile ? '20px' : '50%',
        left: isMobile ? '16px' : '50%',
        right: isMobile ? '16px' : 'auto',
        bottom: isMobile ? '20px' : 'auto',
        transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: isMobile ? '16px' : '32px',
        boxShadow: 24,
        px: isMobile ? 3 : 4,
        py: isMobile ? 3 : 4,
        width: isMobile ? 'auto' : 600,
        maxWidth: isMobile ? 'none' : '90vw',
        maxHeight: isMobile ? 'none' : '75vh',
        height: isMobile ? 'auto' : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    };

    const infoItemSx = {
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 1.5 : 2,
        py: isMobile ? 0.75 : 1,
        '&:not(:last-child)': {
            borderBottom: '1px solid #f0f0f0',
        },
    };

    const labelSx = {
        fontFamily: 'Poppins, sans-serif',
        fontSize: isMobile ? '13px' : '14px',
        fontWeight: 500,
        color: '#666',
        minWidth: isMobile ? 100 : 120,
    };

    const valueSx = {
        fontFamily: 'Poppins, sans-serif',
        fontSize: isMobile ? '13px' : '14px',
        fontWeight: 400,
        color: '#132F5F',
        flex: 1,
    };

    if (!verificationData) {
        return null;
    }

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
            }}
        >
            <Box sx={style}>
                {/* Fixed Header */}
                <Box sx={{ flexShrink: 0, mb: isMobile ? 1.5 : 2 }}>
                    <Box sx={{ position: 'relative', mb: isMobile ? 1.5 : 2 }}>
                        <Stack direction='row' justifyContent='center' alignItems='center' spacing={1}>
                            <VerifiedUserIcon sx={{ color: '#132F5F', fontSize: { xs: 24, sm: 28 } }} />
                            <Typography
                                fontFamily='Poppins, sans-serif'
                                fontWeight={600}
                                fontSize={{ xs: '18px', sm: '20px' }}
                                lineHeight='28px'
                                color='#132F5F'
                            >
                                Verification Results
                            </Typography>
                        </Stack>
                        
                        {/* Close Button */}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                color: '#666',
                                backgroundColor: '#fff',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                },
                                width: 32,
                                height: 32,
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>

                    {/* Success Alert */}
                    <Alert 
                        severity="success" 
                        sx={{ 
                            borderRadius: '12px',
                            fontFamily: 'Poppins, sans-serif',
                            '& .MuiAlert-message': {
                                fontSize: '14px',
                            },
                        }}
                    >
                        Verification completed successfully. Please review the details below.
                    </Alert>
                </Box>

                {/* Scrollable Content */}
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a8a8a8',
                    },
                }}>
                    

                    {/* KYC Data Details */}
                    <Box sx={{ bgcolor: '#f8f9fa', borderRadius: isMobile ? '8px' : '12px', p: isMobile ? 2 : 3 }}>
                        {/* Personal Information Section */}
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize='16px'
                            color='#132F5F'
                            sx={{ mb: 2 }}
                        >
                            Personal Information
                        </Typography>

                        <Stack spacing={0}>
                            {/* Consumer ID */}
                            <Box sx={infoItemSx}>
                                <FingerprintIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                <Typography sx={labelSx}>Consumer ID:</Typography>
                                <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.consumer_id)}</Typography>
                            </Box>

                            {/* Full Name */}
                            <Box sx={infoItemSx}>
                                <PersonIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                <Typography sx={labelSx}>Full Name:</Typography>
                                <Typography sx={valueSx}>
                                    {[
                                        verificationData.kyc_data.first_name,
                                        verificationData.kyc_data.second_name,
                                        verificationData.kyc_data.surname
                                    ]
                                        .filter(name => name && name.trim())
                                        .join(' ') || 'N/A'}
                                </Typography>
                            </Box>

                            {/* Title */}
                            {verificationData.kyc_data.title_desc && (
                                <Box sx={infoItemSx}>
                                    <PersonIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Title:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.title_desc)}</Typography>
                                </Box>
                            )}

                            {/* ID Number */}
                            <Box sx={infoItemSx}>
                                <BadgeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                <Typography sx={labelSx}>ID Number:</Typography>
                                <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.id_number)}</Typography>
                            </Box>

                            {/* Birth Date */}
                            <Box sx={infoItemSx}>
                                <CakeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                <Typography sx={labelSx}>Birth Date:</Typography>
                                <Typography sx={valueSx}>{formatDate(verificationData.kyc_data.birth_date)}</Typography>
                            </Box>

                            {/* Age */}
                            {verificationData.kyc_data.age && (
                                <Box sx={infoItemSx}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Age:</Typography>
                                    <Typography sx={valueSx}>{String(verificationData.kyc_data.age)}</Typography>
                                </Box>
                            )}

                            {/* Gender */}
                            <Box sx={infoItemSx}>
                                {getGenderIcon(verificationData.kyc_data.gender)}
                                <Typography sx={labelSx}>Gender:</Typography>
                                <Typography sx={valueSx}>{getGenderText(verificationData.kyc_data.gender)}</Typography>
                            </Box>

                            {/* Marital Status */}
                            {verificationData.kyc_data.marital_status_desc && (
                                <Box sx={infoItemSx}>
                                    <FavoriteIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Marital Status:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.marital_status_desc)}</Typography>
                                </Box>
                            )}

                            {/* Nationality */}
                            {verificationData.kyc_data.nationality && (
                                <Box sx={infoItemSx}>
                                    <PublicIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Nationality:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.nationality)}</Typography>
                                </Box>
                            )}

                            {/* Privacy Status */}
                            {verificationData.kyc_data.privacy_status && (
                                <Box sx={infoItemSx}>
                                    <VerifiedUserIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Privacy Status:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.privacy_status)}</Typography>
                                </Box>
                            )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* KYC Verification Status Section */}
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize='16px'
                            color='#132F5F'
                            sx={{ mb: 2 }}
                        >
                            KYC Verification Status
                        </Typography>

                        <Stack spacing={0}>
                            {/* ID Validation Status */}
                            {verificationData.kyc_data.kyc_id_status_desc && (
                                <Box sx={infoItemSx}>
                                    <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                    <Typography sx={labelSx}>ID Status:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.kyc_id_status_desc)}</Typography>
                                </Box>
                            )}

                            {/* ID Validity */}
                            {verificationData.kyc_data.kyc_id_validity && (
                                <Box sx={infoItemSx}>
                                    <VerifiedUserIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>ID Validity:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.kyc_id_validity)}</Typography>
                                </Box>
                            )}

                            {/* KYC Match Status */}
                            {verificationData.kyc_data.kyc_status_desc && (
                                <Box sx={infoItemSx}>
                                    <InfoIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Match Status:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.kyc_status_desc)}</Typography>
                                </Box>
                            )}

                            {/* Address Confirmation */}
                            {verificationData.kyc_data.kyc_residential_address_confirmation && (
                                <Box sx={infoItemSx}>
                                    <HomeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Address Confirm:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.kyc_residential_address_confirmation)}</Typography>
                                </Box>
                            )}

                            {/* Total Sources Used */}
                            {verificationData.kyc_data.kyc_total_sources_used !== undefined && (
                                <Box sx={infoItemSx}>
                                    <DataUsageIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Sources Used:</Typography>
                                    <Typography sx={valueSx}>{String(verificationData.kyc_data.kyc_total_sources_used)}</Typography>
                                </Box>
                            )}

                            {/* KYC Sources */}
                            {verificationData.kyc_data.kyc_sources && (
                                <Box sx={infoItemSx}>
                                    <DataUsageIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>KYC Sources:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.kyc_sources)}</Typography>
                                </Box>
                            )}

                            {/* KYC Enquiry Date */}
                            {verificationData.kyc_data.kyc_enquiry_date && (
                                <Box sx={infoItemSx}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>KYC Enquiry Date:</Typography>
                                    <Typography sx={valueSx}>{formatDate(verificationData.kyc_data.kyc_enquiry_date)}</Typography>
                                </Box>
                            )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Contact Information Section */}
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize='16px'
                            color='#132F5F'
                            sx={{ mb: 2 }}
                        >
                            Contact Information
                        </Typography>

                        <Stack spacing={0}>
                            {/* Residential Address */}
                            {verificationData.kyc_data.residential_address && (
                                <Box sx={infoItemSx}>
                                    <HomeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Residential:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.residential_address)}</Typography>
                                </Box>
                            )}

                            {/* Postal Address */}
                            {verificationData.kyc_data.postal_address && (
                                <Box sx={infoItemSx}>
                                    <MailIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Postal:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.postal_address)}</Typography>
                                </Box>
                            )}

                            {/* Home Telephone */}
                            {verificationData.kyc_data.home_telephone_no && (
                                <Box sx={infoItemSx}>
                                    <PhoneIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Home Phone:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.home_telephone_no)}</Typography>
                                </Box>
                            )}

                            {/* Work Telephone */}
                            {verificationData.kyc_data.work_telephone_no && (
                                <Box sx={infoItemSx}>
                                    <PhoneIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Work Phone:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.work_telephone_no)}</Typography>
                                </Box>
                            )}

                            {/* Cellular Number */}
                            {verificationData.kyc_data.cellular_no && (
                                <Box sx={infoItemSx}>
                                    <PhoneIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Mobile:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.cellular_no)}</Typography>
                                </Box>
                            )}

                            {/* Email Address */}
                            {verificationData.kyc_data.email_address && (
                                <Box sx={infoItemSx}>
                                    <EmailIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Email:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.email_address)}</Typography>
                                </Box>
                            )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Employment Information Section */}
                        {verificationData.kyc_data.employer_detail && (
                            <>
                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontWeight={600}
                                    fontSize='16px'
                                    color='#132F5F'
                                    sx={{ mb: 2 }}
                                >
                                    Employment Information
                                </Typography>

                                <Stack spacing={0}>
                                    <Box sx={infoItemSx}>
                                        <WorkIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                        <Typography sx={labelSx}>Employer:</Typography>
                                        <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.employer_detail)}</Typography>
                                    </Box>
                                </Stack>

                                <Divider sx={{ my: 2 }} />
                            </>
                        )}

                        {/* Subscriber Information Section */}
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize='16px'
                            color='#132F5F'
                            sx={{ mb: 2 }}
                        >
                            Subscriber Information
                        </Typography>

                        <Stack spacing={0}>
                            {/* Subscriber Name */}
                            {verificationData.kyc_data.subscriber_name && (
                                <Box sx={infoItemSx}>
                                    <BusinessIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Subscriber:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.subscriber_name)}</Typography>
                                </Box>
                            )}

                            {/* Subscriber User Name */}
                            {verificationData.kyc_data.subscriber_user_name && (
                                <Box sx={infoItemSx}>
                                    <PersonIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>User Name:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.subscriber_user_name)}</Typography>
                                </Box>
                            )}

                            {/* Enquiry Type */}
                            {verificationData.kyc_data.subscriber_enquiry_type && (
                                <Box sx={infoItemSx}>
                                    <SearchIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Enquiry Type:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.subscriber_enquiry_type)}</Typography>
                                </Box>
                            )}

                            {/* Enquiry Input */}
                            {verificationData.kyc_data.subscriber_enquiry_input && (
                                <Box sx={infoItemSx}>
                                    <InfoIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Enquiry Input:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.subscriber_enquiry_input)}</Typography>
                                </Box>
                            )}

                            {/* Subscriber Enquiry Date */}
                            {verificationData.kyc_data.subscriber_enquiry_date && (
                                <Box sx={infoItemSx}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Enquiry Date:</Typography>
                                    <Typography sx={valueSx}>{formatDateTime(verificationData.kyc_data.subscriber_enquiry_date)}</Typography>
                                </Box>
                            )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Reference Information */}
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize='16px'
                            color='#132F5F'
                            sx={{ mb: 2 }}
                        >
                            Reference Information
                        </Typography>

                        <Stack spacing={0}>
                            {/* Reference Number */}
                            {verificationData.kyc_data.reference_no && (
                                <Box sx={infoItemSx}>
                                    <BadgeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>Reference No:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.reference_no)}</Typography>
                                </Box>
                            )}

                            {/* External Reference */}
                            {verificationData.kyc_data.external_reference && (
                                <Box sx={infoItemSx}>
                                    <BadgeIcon sx={{ fontSize: 16, color: '#132F5F' }} />
                                    <Typography sx={labelSx}>External Ref:</Typography>
                                    <Typography sx={valueSx}>{formatValue(verificationData.kyc_data.external_reference)}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </Box>

                {/* Fixed Footer */}
                <Box sx={{ flexShrink: 0, mt: isMobile ? 1.5 : 2 }}>
                    <Stack 
                        direction={isMobile ? 'column' : 'row'} 
                        spacing={isMobile ? 1.5 : 2} 
                        justifyContent="center"
                    >
                        <CancelButton
                            onClick={onCloseAll}
                            sx={{
                                color: '#DE3B40',
                                borderColor: '#DE3B40',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: { xs: 14, sm: 16 },
                                height: { xs: 48, sm: 56 },
                                width: isMobile ? '100%' : 'auto',
                                minWidth: 160,
                                px: { xs: 3, sm: 4 },
                                '&:hover': { 
                                    backgroundColor: '#FFECEC',
                                    borderColor: '#DE3B40',
                                },
                            }}
                        >
                            Close
                        </CancelButton>
                        
                        {showForceUpdate && onForceUpdate && (
                            <ActionButton
                                onClick={onForceUpdate}
                                sx={{
                                    backgroundColor: '#132F5F',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: isMobile ? '100%' : 'auto',
                                    minWidth: 160,
                                    px: { xs: 3, sm: 4 },
                                    '&:hover': { 
                                        backgroundColor: '#0f254d' 
                                    },
                                }}
                            >
                                Force Update
                            </ActionButton>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
};

export default VerificationResultsDialog;
